import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle, Zap, MessageSquare, Mail } from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Mini-profil osoby — landing dla deep-linków z Sorto-CRM (karta kontaktu → "Profil Aurabase").
 * Lookup po email, render profilu inline. Jeśli user nie istnieje w Aurabase → 404.
 */
export default function MemberByEmailPage() {
  const { email: rawEmail } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const email = decodeURIComponent(rawEmail);
        const token = localStorage.getItem('aurabase_token');
        const r = await axios.get(`${API_URL}/users/by-email/${encodeURIComponent(email)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setProfile(r.data);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 404) setError(`Brak profilu Aurabase dla ${decodeURIComponent(rawEmail)}`);
        else setError(e?.response?.data?.detail || e.message);
      }
    })();
  }, [rawEmail]);

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="bg-white border border-rose-200 rounded-lg p-6 max-w-md text-center">
            <AlertCircle className="w-10 h-10 mx-auto mb-3 text-rose-500" />
            <h2 className="font-semibold text-slate-900 mb-2">Nie znaleziono profilu</h2>
            <p className="text-sm text-rose-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/dashboard/communities', { replace: true })}
              className="px-4 py-2 bg-slate-100 text-slate-700 text-sm rounded hover:bg-slate-200"
            >
              Społeczności
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!profile) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-start gap-4">
            <img
              src={profile.avatar || `https://ui-avatars.com/api/?name=${profile.name || profile.email}&background=10b981&color=fff`}
              alt={profile.name}
              className="w-20 h-20 rounded-full"
            />
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-slate-900">{profile.name || profile.email}</h1>
              {profile.email && (
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                  <Mail className="w-3.5 h-3.5" /> {profile.email}
                </p>
              )}
              {profile.bio && <p className="text-slate-700 mt-3">{profile.bio}</p>}
              {profile.aura_score !== undefined && (
                <div className="flex items-center gap-2 mt-3 text-sm">
                  <Zap className="w-4 h-4 text-emerald-500" />
                  <span className="text-slate-600">Aura Score:</span>
                  <span className="font-semibold text-emerald-600">{profile.aura_score}</span>
                </div>
              )}
            </div>
          </div>

          {(profile.skills?.length > 0 || profile.interests?.length > 0) && (
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
              {profile.skills?.length > 0 && (
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase">Skills:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {profile.skills.map((s, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded">#{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {profile.interests?.length > 0 && (
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase">Interests:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {profile.interests.map((s, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded">#{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-slate-100 flex gap-2">
            <button
              onClick={() => navigate('/dashboard/connections', { replace: false })}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              <MessageSquare className="w-4 h-4" /> Zaproś do połączenia
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
